<script setup>
import InputError from '@/Components/Forms/InputError.vue'

const props = defineProps({
  modelValue: {
    type: Object,
    required: true
  },
  errors: {
    type: Object,
    default: () => ({})
  },
  title: {
    type: String,
    default: 'Payment Information'
  },
  description: {
    type: String,
    default: 'Update your bank details for receiving tutor payments'
  },
  readonly: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue'])

const ukBanks = [
  { value: '', label: 'Select your bank' },
  { value: 'barclays', label: 'Barclays' },
  { value: 'hsbc', label: 'HSBC' },
  { value: 'lloyds-bank', label: 'Lloyds Bank' },
  { value: 'natwest', label: 'NatWest' },
  { value: 'royal-bank-of-scotland', label: 'Royal Bank of Scotland' },
  { value: 'santander', label: 'Santander' },
  { value: 'halifax', label: 'Halifax' },
  { value: 'tsb', label: 'TSB' },
  { value: 'nationwide', label: 'Nationwide' },
  { value: 'metro-bank', label: 'Metro Bank' },
  { value: 'first-direct', label: 'First Direct' },
  { value: 'monzo', label: 'Monzo' },
  { value: 'starling-bank', label: 'Starling Bank' },
  { value: 'revolut', label: 'Revolut' },
  { value: 'co-operative-bank', label: 'Co-operative Bank' },
]

const updateField = (field, value) => {
  emit('update:modelValue', { ...props.modelValue, [field]: value })
}

const formatSortCode = (event) => {
  let value = event.target.value.replace(/[^0-9]/g, '')
  if (value.length > 6) value = value.slice(0, 6)

  if (value.length > 4) {
    value = value.slice(0, 2) + '-' + value.slice(2, 4) + '-' + value.slice(4)
  } else if (value.length > 2) {
    value = value.slice(0, 2) + '-' + value.slice(2)
  }

  updateField('sort_code', value)
}

const formatAccountNumber = (event) => {
  let value = event.target.value.replace(/[^0-9]/g, '')
  if (value.length > 8) value = value.slice(0, 8)
  updateField('account_number', value)
}

const formatPostcode = (event) => {
  updateField('postcode', event.target.value.toUpperCase())
}
</script>

<template>
  <section class="bg-white rounded-2xl p-5 md:p-10">
    <div class="mb-8">
      <h2 class="text-2xl font-bold text-gray-900 mb-2">{{ title }}</h2>
      <p class="text-gray-600">{{ description }}</p>
    </div>

    <div class="space-y-6">
      <!-- Account Holder Name -->
      <div id="account-holder-field" class="form-group">
        <label class="block text-sm font-semibold text-gray-700 mb-2">
          Account Holder Name
          <span class="text-red-500 ml-1">*</span>
        </label>
        <input
          :value="modelValue.account_holder_name"
          @input="updateField('account_holder_name', $event.target.value)"
          type="text"
          placeholder="Enter full name as it appears on your account"
          :disabled="readonly"
          :class="[
            'w-full px-4 py-3.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-blue focus:border-transparent outline-none transition-all text-gray-900 placeholder-gray-400',
            readonly ? 'bg-gray-100 cursor-not-allowed' : ''
          ]"
        />
        <InputError :message="errors.account_holder_name" />
        <p v-if="!errors.account_holder_name" class="text-xs text-gray-500 mt-2 flex items-center">
          <span class="material-symbols-outlined text-sm mr-1">info</span>
          Must match the name on your bank account exactly
        </p>
      </div>

      <!-- Sort Code & Account Number -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div id="sort-code-field" class="form-group">
          <label class="block text-sm font-semibold text-gray-700 mb-2">
            Sort Code
            <span class="text-red-500 ml-1">*</span>
          </label>
          <input
            :value="modelValue.sort_code"
            @input="formatSortCode"
            type="text"
            placeholder="XX-XX-XX"
            maxlength="8"
            :disabled="readonly"
            :class="[
              'w-full px-4 py-3.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-blue focus:border-transparent outline-none font-mono text-lg tracking-wider transition-all text-gray-900',
              readonly ? 'bg-gray-100 cursor-not-allowed' : ''
            ]"
          />
          <InputError :message="errors.sort_code" />
          <p v-if="!errors.sort_code" class="text-xs text-gray-500 mt-2 flex items-center">
            <span class="material-symbols-outlined text-sm mr-1">info</span>
            6-digit code from your bank card
          </p>
        </div>

        <div id="account-number-field" class="form-group">
          <label class="block text-sm font-semibold text-gray-700 mb-2">
            Account Number
            <span class="text-red-500 ml-1">*</span>
          </label>
          <input
            :value="modelValue.account_number"
            @input="formatAccountNumber"
            type="text"
            placeholder="XXXXXXXX"
            maxlength="8"
            :disabled="readonly"
            :class="[
              'w-full px-4 py-3.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-blue focus:border-transparent outline-none font-mono text-lg tracking-wider transition-all text-gray-900',
              readonly ? 'bg-gray-100 cursor-not-allowed' : ''
            ]"
          />
          <InputError :message="errors.account_number" />
          <p v-if="!errors.account_number" class="text-xs text-gray-500 mt-2 flex items-center">
            <span class="material-symbols-outlined text-sm mr-1">info</span>
            Usually 8 digits long
          </p>
        </div>
      </div>

      <!-- Bank Name -->
      <div id="bank-name-field" class="form-group">
        <label class="block text-sm font-semibold text-gray-700 mb-2">
          Bank Name
          <span class="text-red-500 ml-1">*</span>
        </label>
        <div class="relative">
          <select
            :value="modelValue.bank_name"
            @change="updateField('bank_name', $event.target.value)"
            :disabled="readonly"
            :class="[
              'w-full px-4 py-3.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-blue focus:border-transparent outline-none appearance-none transition-all text-gray-900',
              readonly ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'
            ]"
          >
            <option v-for="bank in ukBanks" :key="bank.value" :value="bank.value">
              {{ bank.label }}
            </option>
          </select>
          <span class="material-symbols-outlined absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
            expand_more
          </span>
        </div>
        <InputError :message="errors.bank_name" />
      </div>

      <!-- Billing Address -->
      <div id="address-fields" class="space-y-6 pt-6 border-t border-gray-200">
        <h3 class="text-lg font-semibold text-gray-900 flex items-center">
          <span class="material-symbols-outlined text-brand-blue mr-2">location_on</span>
          Billing Address
        </h3>

        <div id="address-line-1" class="form-group">
          <label class="block text-sm font-semibold text-gray-700 mb-2">
            Address Line 1
            <span class="text-red-500 ml-1">*</span>
          </label>
          <input
            :value="modelValue.address_line_1"
            @input="updateField('address_line_1', $event.target.value)"
            type="text"
            placeholder="Street address, P.O. box"
            :disabled="readonly"
            :class="[
              'w-full px-4 py-3.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-blue focus:border-transparent outline-none transition-all text-gray-900',
              readonly ? 'bg-gray-100 cursor-not-allowed' : ''
            ]"
          />
          <InputError :message="errors.address_line_1" />
        </div>

        <div id="address-line-2" class="form-group">
          <label class="block text-sm font-semibold text-gray-700 mb-2">
            Address Line 2
            <span class="text-gray-400 text-xs ml-1">(Optional)</span>
          </label>
          <input
            :value="modelValue.address_line_2"
            @input="updateField('address_line_2', $event.target.value)"
            type="text"
            placeholder="Apartment, suite, unit, building, floor, etc."
            :disabled="readonly"
            :class="[
              'w-full px-4 py-3.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-blue focus:border-transparent outline-none transition-all text-gray-900',
              readonly ? 'bg-gray-100 cursor-not-allowed' : ''
            ]"
          />
        </div>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div id="city-field" class="form-group col-span-1">
            <label class="block text-sm font-semibold text-gray-700 mb-2">
              City
              <span class="text-red-500 ml-1">*</span>
            </label>
            <input
              :value="modelValue.city"
              @input="updateField('city', $event.target.value)"
              type="text"
              placeholder="City"
              :disabled="readonly"
              :class="[
                'w-full px-4 py-3.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-blue focus:border-transparent outline-none transition-all text-gray-900',
                readonly ? 'bg-gray-100 cursor-not-allowed' : ''
              ]"
            />
            <InputError :message="errors.city" />
          </div>

          <div id="county-field" class="form-group col-span-1">
            <label class="block text-sm font-semibold text-gray-700 mb-2">
              County
              <span class="text-gray-400 text-xs ml-1">(Optional)</span>
            </label>
            <input
              :value="modelValue.county"
              @input="updateField('county', $event.target.value)"
              type="text"
              placeholder="County"
              :disabled="readonly"
              :class="[
                'w-full px-4 py-3.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-blue focus:border-transparent outline-none transition-all text-gray-900',
                readonly ? 'bg-gray-100 cursor-not-allowed' : ''
              ]"
            />
            <InputError :message="errors.county" />
          </div>

          <div id="postcode-field" class="form-group col-span-1">
            <label class="block text-sm font-semibold text-gray-700 mb-2">
              Postcode
              <span class="text-red-500 ml-1">*</span>
            </label>
            <input
              :value="modelValue.postcode"
              @input="formatPostcode"
              type="text"
              placeholder="Postcode"
              :disabled="readonly"
              :class="[
                'w-full px-4 py-3.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-blue focus:border-transparent outline-none transition-all text-gray-900 uppercase',
                readonly ? 'bg-gray-100 cursor-not-allowed' : ''
              ]"
            />
            <InputError :message="errors.postcode" />
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
